import * as Main from '@1js/search-page/dist/amd/lib/main';
import { IMainSettings } from '@1js/search-page/dist/amd/lib/main.settings';
import { Injectable } from '@angular/core';

import { SearchConfigService } from '../search-results/services/search-config.service';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { FastSearchUXLoggerSettingsService } from './fast-searchux-logger-settings.service';

@Injectable({ providedIn: 'root' })
export class FastSearchUXMainSettingsService {

    constructor(
        private readonly capabilitesService: CapabilitiesService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly contextService: ContextService,
        private readonly searchConfigService: SearchConfigService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly loggerSettingsService: FastSearchUXLoggerSettingsService) {}

    public createSettings(
        clientId: string,
        environment: string,
        instrumenter: Main.SearchInstrumenter): IMainSettings {

        // TODO: Enable when oneClick doesn't throw any errors for Excel file types
        //conf.sets('searchux.extension.fileClickTargetWrapper', main.OneUpPreviewTokenFileClickTargetWrapper);
        // This setting is for separating out your app in telemetry data and SSR calculations
        // conf.sets('searchux.clientId', 'officehome');
        // This setting is used to separate out cached data for different versions of search.
        // conf.sets('searchux.clientVersion', 'officehome-1.0.0');

        // This array should contain the flight headers that always need to be sent to the 3S API calls
        const flights3s: Array<string> = [
            'OfficeComPeople'
        ];

        // Check to see if the DYM feature is enabled and add the flight header for 3S if true
        if (this.searchConfigService.config.serpSpellCheckEnabled) {
            flights3s.push('SPOBingSpeller');
        }

        return {
            ...this.loggerSettingsService.createSettings(),
            'searchux.answerBlock': this.searchConfigService.config.fastBingAnswerResultsEnabled,
            'searchux.answerBlockExperiment': this.searchConfigService.config.fastBingAnswerResultsEnabled,
            'searchux.api.sharePoint.url': this.contextService.sharedContext.sharePointResourceId,
            'searchux.clientId': clientId,
            'searchux.clientSideRelevance': true,
            'searchux.clientVersion': 'officehomeweb',
            'searchux.component.TriageNewsSearchResult.previewEnabled': false,
            'searchux.currentLanguage': this.contextService.sharedContext.officeMarketLcid as any,
            'searchux.debug': this.clientStartupConfigService.config.angularDebugInfoEnabled,
            'searchux.enabled': true,
            'searchux.environment': environment,
            'searchux.isCrossGeoEnabled': true,
            'searchux.isBetaTagEnabled': false,
            'searchux.isTokenBasedFileThumbnailsEnabled': true,
            'searchux.isTokenBasedProfileImagesEnabled': true,
            'searchux.locale': this.contextService.sharedContext.officeMarket,
            'searchux.showPersonalResults': this.searchConfigService.config.fastShowPersonalResultsEnabled,
            'searchux.substrateSearchClientFlights': flights3s.join(','),
            'searchux.substrateSuggestionsScenario': 'officehome',
            'searchux.triageSiteSearchResult.siteLogo.use': true,
            'searchux.use3SEcho': false,
            'searchux.use3SFileSuggestions': true,
            'searchux.use3SFileSuggestionsExperiment': true,
            'searchux.use3SPeopleSuggestions': true,
            'searchux.use3SPeopleSuggestionsExperiment': true,
            'searchux.use3SSiteSuggestions': true,
            'searchux.use3SSiteSuggestionsExperiment': true,
            'searchux.useSearchBoxInTheSuiteNav': this.searchConfigService.config.searchInHeaderEnabled,
            'searchux.useSearchBoxInTheSuiteNavExperiment': this.searchConfigService.config.searchInHeaderEnabled,
            'searchux.useSubstrateSearch': this.searchConfigService.config.fastUseSubstrateEnabled,
            'searchux.userEmail': this.clientStartupConfigService.config.userPrincipalName,
            'searchux.userRing': 'Local',
            'searchux.enableQueryRules': this.searchConfigService.config.fastEnableQueryRules,
            'searchux.noSourceIdForFiles': true,
            'searchux.enableStartQuery': false,
            'searchux.enableStartAnswersQuery': false,
            'searchux.isVideoVerticalEnabled': false,
            'searchux.instrumenter': instrumenter,
            'searchux.excludeBestBets': false,
            'searchux.showDYM': this.searchConfigService.config.serpSpellCheckEnabled,
            'searchux.isOfficePwaInUwp': this.capabilitesService.capabilities.isPwaInUwp
        };
    }

    public async createSettingsForConsumerSERP(
        clientId: string,
        environment: string,
        instrumenter: Main.SearchInstrumenter
        ): Promise<IMainSettings> {

        return {
            'searchux.clientId': clientId,
            'searchux.clientSideRelevance': false,
            'searchux.clientVersion': 'officehomeweb',
            'searchux.currentLanguage': this.contextService.sharedContext.officeMarketLcid as any,
            'searchux.enableConfiguredOOBRefiners': true,
            'searchux.enabled': true,
            'searchux.environment': environment,
            'searchux.isConsumerAllVerticalEnabled': true,
            'searchux.isConsumerFileVerticalEnabled': true,
            'searchux.isConsumerTemplateVerticalEnabled': true,
            'searchux.instrumenter': instrumenter,
            'searchux.isCrossGeoEnabled': false,
            'searchux.locale': this.contextService.sharedContext.officeMarket,
            'searchux.showPersonalResults': false,
            'searchux.use3SFileSuggestions': false,
            'searchux.use3SPeopleSuggestions': false,
            'searchux.use3SSiteSuggestions': false,
            'searchux.userEmail': this.clientStartupConfigService.config.userPrincipalName,
            'searchux.userRing': 'Local',
            'searchux.useSearchApp': false,
            'searchux.isContextualRailEnabled': true,
            'searchux.shouldTemplateVerticalShowPremiumTemplates': this.searchConfigService.config.premiumTemplatesForSearchEnabled,
            'searchux.isSubscribedUser': await this.isPaidConsumer()
        };
    }

    public async isPaidConsumer(): Promise<boolean> {
        if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
            const skuType = await this.consumerInstallConfigService.getCurrentSku();
            return (skuType !== null) && !(skuType === ConsumerSkuType.NonActive || skuType === ConsumerSkuType.NotSubscribed || skuType === ConsumerSkuType.NotDetected || skuType === ConsumerSkuType.Perpetual || skuType === ConsumerSkuType.Perpetual2019);
        }
        return false;
    }
}
